import type { AuditRecord } from "@tdelivr/api-client/generated/security/audit.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

/**
 * What the platform audit board does to the records AUD-01 returns, away from any rendering.
 *
 * The same split the tenant administration console's `tenant-audit.ts` makes, for the same reason:
 * searching, ordering, paging and deciding what a record's state is can all be stated and tested
 * without a browser, and a component that does them inline can only be checked by rendering it.
 *
 * **This is not that module with the names changed, and the three differences are the whole point
 * of the screen.**
 *
 *  1. **It reads `GET /v1/platform/audit-records`, not `GET /v1/audit-records`.** Those are two
 *     operations with two scopes, published deliberately side by side by
 *     [ADR 0039](../../../docs/adr/0039-a-platform-scoped-audit-read-is-a-separate-operation-and-a-separate-scope.md).
 *     The tenant-bounded one restricts every row to the verified token's tenant, which is correct
 *     for a tenant administrator and is the one thing a *platform* audit board must not do — the
 *     page's own purpose line is "Who did what, **across every tenant**".
 *  2. **`tenantId` is a column here**, drawn on every row. On the tenant admin board it is a
 *     constant and is not drawn; here it is the field that makes the board a platform board, and the
 *     design writes it as `K. Otieno · ten_7Q2M` — the actor and the tenant in one cell.
 *  3. **`cross-tenant` is decided differently.** The tenant admin board reads `actorChain[].kind ===
 *     "impersonator"`, because on a tenant-bounded listing every row belongs to that tenant and the
 *     only way a platform operator appears is by acting as somebody in it. On a listing that spans
 *     tenants, a platform staff member acting in their own right is a row whose `tenantId` is not
 *     the one they belong to — and the design's own third row is exactly that: `S. Banda · platform`
 *     carrying a `cross-tenant` badge, with no impersonation anywhere in it. **That second reading
 *     is not derivable**, because an audit record does not publish the actor's own tenant; so
 *     `crossTenantActor()` below reads the half that is published and says so, and the `Cross-tenant
 *     actions · 37` tile has no source. Q-174.
 */

/** AUD-01's published outcome enum, as one name. */
export type AuditOutcome = AuditRecord["outcome"];

/** A link in `actorChain`, as AUD-01 publishes it. */
export type AuditActor = AuditRecord["actorChain"][number];

/**
 * How many records to take in one request.
 *
 * 200 is AUD-01's own maximum, on both of its listings: `limit: integer, min 1, max 200, default
 * 50`. The design's own KPI tile for this page reads `Events today · 92,118` — eleven times the
 * tenant admin page's figure, because this listing spans every tenant — so the board is a window on
 * the log rather than the log, and `boundedNote()` says so on screen rather than leaving it implied.
 *
 * A string because every query parameter reaches the wire as one, and the generated client types
 * them that way rather than converting on the caller's behalf.
 */
export const PER_FETCH = 200;
export const PER_FETCH_PARAMETER = String(PER_FETCH);

/**
 * Whether a row is a platform-staff action rather than a tenant's own.
 *
 * The design's word for it is `platform`, written where a tenant identifier would be:
 * `S. Banda · platform`. Nothing in the contract publishes a boolean for that, and nothing needs to
 * — `actorChain[].subject` is an identity subject and `tenantId` is the tenant the *record* belongs
 * to, so the question "did somebody from outside this tenant do this" is a comparison rather than a
 * field.
 *
 * **It cannot be made from the row alone, and that is the honest limit of this function.** A
 * subject's home tenant is not published anywhere on an audit record: there is no `actorTenantId`,
 * and `producerSubject` is the service that reported the fact rather than the actor's employer. So
 * what *is* derivable is the half the design draws a badge for and the half it does not:
 *
 *   - **impersonation**, which is published — `kind: "impersonator"` beside `user` and `service` —
 *     and which is somebody acting as somebody else by construction;
 *   - **a service acting alone**, which is published and is not a person at all.
 *
 * Neither of those is "a platform staff member acting in their own right on another tenant's data",
 * which is the design's third row. That reading needs a subject-to-tenant mapping the platform does
 * not publish, and inventing one — treating an unfamiliar subject shape as platform staff, say —
 * would be a badge whose meaning this file made up. Recorded as **Q-174**: the row is drawn with the
 * state its published fields support, and the `Cross-tenant actions · 37` tile has no source.
 *
 * Any link, not only the first. An impersonator deeper in the chain is still somebody acting as
 * somebody else, and a chain is recorded outermost-in by whoever wrote it — nothing in the contract
 * fixes which end the impersonator sits at.
 */
export function crossTenantActor(record: AuditRecord): boolean {
  return record.actorChain.some((actor) => actor.kind === "impersonator");
}

/**
 * The design's `Actor` cell: who did the thing, the tenant it was done in, not who reported it.
 *
 * Two published fields and one bar, which is the design's own grammar — `K. Otieno · ten_7Q2M`.
 *
 * **An impersonator in the chain is the actor, and getting this backwards is the one way this column
 * can lie.** The design's third row names the platform staff member, not the tenant user whose
 * identity they borrowed. A cell that drew the innermost link instead would attribute a
 * platform-staff action to the tenant's own operator — on the page whose whole purpose is naming who
 * did what.
 *
 * Otherwise the innermost link, because that is the actor on whose behalf everything outside it was
 * acting. Its `kind` travels with it: `user` is left unqualified, as the design's `K. Otieno` is, and
 * `service` is named, because a service acting alone is a fact a platform operator reading the log
 * needs on the row.
 *
 * `subject` is an identity subject, not a name. **Nothing on this platform turns a workforce subject
 * into a person's name** — the design draws `K. Otieno`, `A. Mwangi` and `S. Banda`, and the staff
 * roster that would hold those names does not exist on any console: IAM-02 links external subjects
 * to providers and holds no name at all. So the subject is shown as published, which is R-12's
 * finding on the tenant admin board and is unchanged here.
 */
export function actorOf(record: AuditRecord): string {
  const impersonator = record.actorChain.find((link) => link.kind === "impersonator");
  const actor = impersonator ?? record.actorChain.at(-1);
  if (actor === undefined) return "no actor recorded";
  if (actor.kind === "user") return actor.subject;
  return `${actor.subject} · ${actor.kind}`;
}

/**
 * The tenant each row belongs to, which is the field that makes this a platform board.
 *
 * Published on every row of the platform listing — `auditRecords.tenantId`, required — and the one
 * element whose absence had `platform · Platform audit` recorded as blocked until ADR 0039. Shown as
 * published: it is a tenant identifier, and nothing on this platform turns one into a tenant's name
 * from a console. TEN-01 publishes no `GET /v1/tenants`, which is also why the `Tenant list` page
 * beside this one is blocked, so there is no roster to resolve it against. The design writes
 * `ten_7Q2M`, an identifier, so the design agrees.
 */
export function tenantOf(record: AuditRecord): string {
  return record.tenantId;
}

/**
 * The state class and the word, from `outcome` and the actor chain together.
 *
 * The design's three sample rows settle both the mapping and its precedence, which is why this is
 * read off the design rather than decided here:
 *
 * | the design's row                                       | class     | word           |
 * | ------------------------------------------------------ | --------- | -------------- |
 * | `payout PYO-0928 approved`                             | `done`    | `recorded`     |
 * | `payout approve refused (same person)`                 | `closed`  | `denied`       |
 * | `replay RPL-0311 approved` by `S. Banda · platform`    | `caution` | `cross-tenant` |
 *
 * The third row fixes the precedence: its outcome is a success and its class is `caution`, so being
 * a cross-tenant action outranks having succeeded. It does **not** outrank having been refused — the
 * second row is a denial, and a denial is the stronger fact either way: an operator scanning for
 * what went wrong needs refusals, and a refused cross-tenant action is still a refusal.
 *
 * `failed` has no sample row. AUD-01 publishes three outcomes and the design draws two of them, so
 * `failed` takes the design's `failed` class and the contract's own word — the class is solid and
 * sorts second, which is right for an action that was attempted and did not work.
 *
 * Identical to the tenant admin board's mapping, and that is not duplication by accident: the design
 * files carry the same three rows with the same two badges, so a board that classified them
 * differently would be two consoles disagreeing about one log.
 */
export function stateOf(record: AuditRecord): { classKey: StateClassKey; label: string } {
  if (record.outcome === "denied") return { classKey: "closed", label: "denied" };
  if (record.outcome === "failed") return { classKey: "failed", label: "failed" };
  if (crossTenantActor(record)) return { classKey: "caution", label: "cross-tenant" };
  return { classKey: "done", label: "recorded" };
}

/**
 * The design's `When` column: a clock time, `09:12:04`.
 *
 * `occurredAt` rather than `ingestedAt`, because `occurredAt` is what the record is *about* and is
 * the column AUD-01 orders by — a board whose rows were timestamped on one clock and sorted on
 * another would look shuffled. Both are published, and the gap between them is
 * `ingestionLagSeconds()` below.
 *
 * Rendered in the viewer's own zone by `Intl`, as a 24-hour clock with seconds, which is what the
 * design writes. The date is not in the design's cell and is not added.
 *
 * **On a cross-tenant board that omission costs more than it does on a tenant one**, and it is
 * recorded rather than fixed: 92,118 events a day across every tenant means the newest 200 can span
 * well under a minute, so a clock with no date is unambiguous — but the same board filtered to a
 * quiet hour can reach yesterday without saying so. Q-175.
 */
export function clockTime(record: AuditRecord, zone?: string): string {
  const at = Date.parse(record.occurredAt);
  if (!Number.isFinite(at)) return record.occurredAt;
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    ...(zone === undefined ? {} : { timeZone: zone }),
  }).format(at);
}

/**
 * How long after it happened the log was told, in seconds, or `undefined` when either end is
 * unreadable.
 *
 * Not in the design, and not drawn as a column — it is on the row's title, where an operator who
 * wonders why a record appears out of order with its neighbours can find it. Both ends are published
 * and the distinction matters on an audit log specifically: `occurredAt` is supplied by whoever
 * reported the fact and is covered by the record's `contentHash`, while `ingestedAt` is stamped by
 * AUD-01 when it received it. A producer with a wrong clock, or one that batches, writes a record
 * that sorts among rows it did not happen between — and on a listing spanning every tenant, that
 * producer is one of 142 services with 142 clocks rather than one tenant's.
 */
export function ingestionLagSeconds(record: AuditRecord): number | undefined {
  const occurred = Date.parse(record.occurredAt);
  const ingested = Date.parse(record.ingestedAt);
  if (!Number.isFinite(occurred) || !Number.isFinite(ingested)) return undefined;
  return Math.round((ingested - occurred) / 1000);
}

/*
 * **There is no `searchableText` here, and its absence is deliberate rather than an omission.**
 *
 * The tenant administration console's `tenant-audit.ts` has one, because that screen is built on its
 * own 500-line board which searches the records directly. This screen is built on `RecordsBoard`, and
 * `board.ts`'s own `searchableText` searches a row's drawn cells, its badge word **and** its detail
 * panel's fields — so `correlationId`, `eventId`, the subject and the resource are all searchable
 * here, under the one implementation, rather than under a second one this file would also have to
 * keep in step.
 *
 * A copy of it lived here until `eslint` reported it as unused, which is the only reason it was
 * found: it compiled, it was correct, and nothing called it. Two implementations of "what does a
 * search match on this board" is exactly the drift `board.ts` exists to prevent.
 */

/** The states present in what came back, for the design's filter, in board order. */
export function statesPresent(rows: readonly AuditRecord[]): readonly string[] {
  return [...new Set(rows.map((record) => stateOf(record).label))];
}

/**
 * Which tenants the rows on screen belong to, and why there is no filter for them.
 *
 * **There is not one, and that is section 1 rather than an oversight.** The design's grid toolbar on
 * every one of the design file's sixteen pages has exactly four controls — a search, one `All states`
 * select, a sort and an export — and the tenant select appears only inside the `Export platform
 * audit log` form. A board spanning every tenant *wants* a tenant filter, this one is the only page
 * in the design where such a control would have a job, and adding it is adding something the design
 * does not draw. So the tenants are derived for one purpose — the sentence under the grid, which
 * says how many tenants the newest 200 records actually came from — and not offered as a control.
 * Recorded as **Q-177**.
 */
export function tenantsPresent(rows: readonly AuditRecord[]): readonly string[] {
  return [...new Set(rows.map((record) => tenantOf(record)))];
}

/**
 * How many tenants the rows on screen came from, in the design's own `—` grammar when there are
 * none.
 *
 * Said because the page's purpose is "across every tenant" and the board cannot show that it is:
 * nothing publishes how many tenants exist (`POST /v1/tenants` exists and there is no `GET`, which
 * is why the `Tenant list` page beside this one is blocked), so "5 of 7 tenants" is unsayable and
 * "records from 3 tenants" is exactly true.
 */
export function tenantSpanNote(rows: readonly AuditRecord[] | undefined): string | undefined {
  if (rows === undefined || rows.length === 0) return undefined;
  const count = tenantsPresent(rows).length;
  return count === 1
    ? "Every record on screen belongs to one tenant. Nothing publishes how many tenants exist, so this board cannot say whether that is all of them."
    : `These records come from ${String(count)} tenants. Nothing publishes how many tenants exist, so this board cannot say whether that is all of them.`;
}

/**
 * Whether paging this listing with the only cursor it publishes would skip a record.
 *
 * **The same defect the tenant admin board found, and worse here by construction.** AUD-01 orders
 * both listings by `(occurred_at DESC, event_id DESC)` and publishes one cursor, `before`, a bare
 * `date-time` applied as `occurred_at < $before`. So a second page asked for with the last row's
 * `occurredAt` begins strictly before that instant — and every record sharing it, including ones
 * this page did not show, is skipped. There is no `nextBefore` in either response, so the cursor has
 * to be synthesised from the last row, which is what makes it unsafe rather than merely awkward.
 *
 * Why worse: records sharing an instant are a function of how many producers are writing. A
 * tenant-bounded page sees one tenant's bursts; this one sees every tenant's, and the design's own
 * tile puts it at 92,118 events a day. A shared millisecond is not an edge case on this listing, it
 * is the expected case.
 *
 * So this board does not page, and says so. R-12 recorded the fix the platform needs — a composite
 * cursor, as the exceptions and manifest listings already have; R-59 records that ADR 0039 published
 * a second listing with the first one's cursor surface unchanged, so the defect was copied rather
 * than inherited.
 *
 * @returns the shared instant, when the last row of a full page shares its `occurredAt` with the row
 * before it; `undefined` when paging would be safe or there is nothing more to page.
 */
export function pageBoundaryHazard(
  rows: readonly AuditRecord[],
  perFetch: number = PER_FETCH,
): string | undefined {
  // A short page is the whole log: there is nothing after it to page to, so there is nothing to
  // lose. The hazard is a property of continuing, not of the rows.
  if (rows.length < perFetch) return undefined;
  const last = rows.at(-1);
  const previous = rows.at(-2);
  if (last === undefined || previous === undefined) return undefined;
  return last.occurredAt === previous.occurredAt ? last.occurredAt : undefined;
}

/**
 * The one or two sentences this board owes an operator about what it actually fetched.
 *
 * The design gives the grid no such line. Every console board on this platform adds one for the
 * reason R-08 defends — an operator working a partial board while believing it whole is the failure
 * it exists to prevent — and on a cross-tenant audit log that reason is at its strongest, because
 * the page's own purpose is completeness across every tenant.
 *
 * Two facts, in order of how much they change what an operator should conclude:
 *
 *  1. **The board is the newest 200 across every tenant, and there is no second page.**
 *     `pageBoundaryHazard` is why.
 *  2. **The filters and the search are over those 200 only.** The listing takes no filter, so
 *     choosing a tenant or `denied` narrows what is on screen rather than asking the service.
 *
 * @param fetched how many rows came back, or `undefined` before anything has.
 * @param hazard the instant `pageBoundaryHazard` found, when the page ends inside a burst.
 * @param perFetch the limit asked for, so a full page can be told from a complete log.
 */
export function boundedNote(
  fetched: number | undefined,
  hazard?: string,
  perFetch = PER_FETCH,
): string | undefined {
  if (fetched === undefined) return undefined;
  if (fetched >= perFetch) {
    const sentence = `This is the newest ${String(perFetch)} records across every tenant, which is the most AUD-01 answers in one request. The search and the state filter cover these ${String(perFetch)} and not the whole log.`;
    return hazard === undefined
      ? sentence
      : `${sentence} It also ends inside a group of records sharing ${hazard}, and the platform listing's only cursor is a plain instant — so asking for the next page would skip the rest of that group. This board does not ask. See R-12 and R-59.`;
  }
  return "GET /v1/platform/audit-records takes no search and no state filter, so both narrow the records already on screen rather than asking the service.";
}

/**
 * Why the designed export form is drawn and refused.
 *
 * The design's `Export audit log` form collects `From`, `To`, a tenant and a format, and the one
 * element of this screen that ADR 0039 left unserved is the date range: the platform listing
 * publishes `limit` and `before`, which is the tenant-bounded read's query surface exactly, because
 * the decision taken was the tenancy boundary and nothing else. A `from`/`to` pair is a second
 * decision about an export, recorded as **Q-140**.
 *
 * So the control is drawn with this sentence on it rather than removed — `CLAUDE.md` section 1 says
 * the add or edit form is not to be removed — and rather than wired to a range the service would
 * ignore, which would produce a file the operator believed was the range they asked for. The grid's
 * own CSV, Excel and PDF exports still work and still export what is on screen; this is the
 * designed *server-side* export of a date range, which is a different thing.
 */
export const EXPORT_REFUSAL =
  "GET /v1/platform/audit-records publishes limit and before only, so a From and To range has nowhere to go. Q-140. The grid's own Export writes what is on screen.";
